import type { UseQueryOptions } from '@tanstack/react-query';
import type { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { CallInfo } from '@thinkalpha/language-services';
import type {
    Sample,
    ConcreteIndicator,
    BulkIndicatorResponseDictionary,
    IndicatorRecord,
    IndicatorRef,
    IndicatorImport,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    AllIndicatorVersionsViewModel,
    ConcreteIndicatorViewModel,
    ImportsStatusesViewModel,
    IndicatorAndReferencesViewModel,
    IndicatorFilterViewModel,
    IndicatorImportViewModel,
    IndicatorImportRefViewModel,
    IndicatorPatchViewModel,
    IndicatorRefViewModel,
    NewIndicatorViewModel,
    TagViewModel,
    TagFilterViewModel,
    TagTreeLevelViewModel,
} from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type { ResourceQuery, ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import Dataloader from 'dataloader';
import { of, pipe } from 'rxjs';
import type { Observable } from 'rxjs';
import { bufferTime, catchError, filter, map, mergeMap } from 'rxjs/operators';
import { rxapi, api } from 'src/api';
import { processQuery } from 'src/lib/paging';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'dictionary-tag-tree' });

export function getTagTreeLevel(
    parentsAndMe: string[],
    includeIndicators: boolean,
    filters: IndicatorFilterViewModel = {},
): Observable<TagTreeLevelViewModel> {
    return rxapi
        .get<TagTreeLevelViewModel>('/dictionary/tags/tree', {
            params: { parentsAndMe, includeIndicators, ...filters },
        })
        .pipe(map((x) => x.data));
}

export function getTagTreeLevelP(
    parentsAndMe: string[],
    includeIndicators: boolean,
    filters: IndicatorFilterViewModel = {},
): Promise<TagTreeLevelViewModel> {
    return api
        .get<TagTreeLevelViewModel>('/dictionary/tags/tree', {
            params: { parentsAndMe, includeIndicators, ...filters },
        })
        .then((x) => x.data);
}

export async function getIndicatorViewModels(
    filters: IndicatorFilterViewModel = {},
): Promise<ConcreteIndicatorViewModel[]> {
    const response = await api.get<ConcreteIndicatorViewModel[]>('/dictionary/indicators', {
        params: { ...filters },
    });

    if (!response.data) {
        log.error('No data returned from getIndicators', {
            filters,
            response,
        });

        return [];
    }

    return response.data;
}

export function getIndicatorsViewModelQuery(
    query: ResourceQuery & { includeMeta: true },
): Promise<ResourceQueryResponseWithMeta<ConcreteIndicatorViewModel>>;
export function getIndicatorsViewModelQuery(query: ResourceQuery): Promise<ConcreteIndicatorViewModel[]>;
export async function getIndicatorsViewModelQuery(
    query: ResourceQuery,
): Promise<ConcreteIndicatorViewModel[] | ResourceQueryResponseWithMeta<ConcreteIndicatorViewModel>> {
    return (await api.get(`/dictionary/query${processQuery(query)}`)).data;
}

export function getIndicatorsQuery(
    query: ResourceQuery & { includeMeta: true },
): Promise<ResourceQueryResponseWithMeta<ConcreteIndicator>>;
export function getIndicatorsQuery(query: ResourceQuery): Promise<ConcreteIndicator[]>;
export async function getIndicatorsQuery(
    query: ResourceQuery,
): Promise<ConcreteIndicator[] | ResourceQueryResponseWithMeta<ConcreteIndicator>> {
    return (await api.get(`/dictionary/query${processQuery(query)}`)).data;
}

export async function getIndicatorViewModelById(
    id: string,
): Promise<IndicatorAndReferencesViewModel<IndicatorImportViewModel>> {
    const indicators = await api.get<IndicatorAndReferencesViewModel<IndicatorImportViewModel>>(
        `/dictionary/indicators/${id}`,
    );
    return indicators.data;
}

export async function getIndicatorById(id: string): Promise<ConcreteIndicator> {
    const indicators = await api.get<ConcreteIndicator>(`/dictionary/indicators/${id}`);
    return indicators.data;
}

export function getIndicatorByIdQueryKey(indicator: string | IndicatorRecord | IndicatorRef): ['indicator', string] {
    return ['indicator', typeof indicator === 'string' ? indicator : indicator.id];
}

export function getIndicatorByIdQuery(
    indicator: string | IndicatorRecord | IndicatorRef,
    options?: Partial<UseQueryOptions<ConcreteIndicator>>,
): UseQueryOptions<ConcreteIndicator> {
    return {
        queryFn: () => getIndicatorById(typeof indicator === 'string' ? indicator : indicator.id),
        queryKey: getIndicatorByIdQueryKey(indicator),
        ...options,
    };
}

export async function getConcreteIndicatorsByIds(ids: string[]): Promise<BulkIndicatorResponseDictionary> {
    if (!ids.length) return {};

    const response = await api.post<BulkIndicatorResponseDictionary>(`/dictionary/bulk`, {
        indicatorIds: ids,
    });
    return response.data;
}

export async function getSuggestedIndicators(): Promise<ConcreteIndicatorViewModel[]> {
    return (await api.get<ConcreteIndicatorViewModel[]>('/dictionary/suggested')).data;
}

export async function getFeaturedIndicators(): Promise<ConcreteIndicator[]> {
    return (await api.get<ConcreteIndicator[]>('/dictionary/featured')).data;
}

export function createIndicator(
    indicator: NewIndicatorViewModel,
    expandImports: true,
): Observable<ConcreteIndicatorViewModel<IndicatorImportViewModel>>;
export function createIndicator(indicator: NewIndicatorViewModel): Observable<ConcreteIndicatorViewModel>;
export function createIndicator(
    indicator: NewIndicatorViewModel,
    expandImports?: true,
): Observable<ConcreteIndicatorViewModel> {
    return rxapi
        .post<ConcreteIndicatorViewModel>(`/dictionary/indicators${expandImports ? '?$expand=imports' : ''}`, indicator)
        .pipe(map((x) => x.data));
}

export function updateIndicator(
    patch: IndicatorPatchViewModel,
    id: string,
    expandImports: true,
): Observable<ConcreteIndicatorViewModel<IndicatorImportViewModel>>;
export function updateIndicator(patch: IndicatorPatchViewModel, id: string): Observable<ConcreteIndicatorViewModel>;
export function updateIndicator(
    patch: IndicatorPatchViewModel,
    id: string,
    expandImports?: true,
): Observable<ConcreteIndicatorViewModel> {
    return rxapi
        .patch<ConcreteIndicatorViewModel>(
            `/dictionary/indicators/${id}${expandImports ? '?$expand=imports' : ''}`,
            patch,
        )
        .pipe(map((x) => x.data));
}

export async function getAllIndicatorVersions(ids: string[]): Promise<AllIndicatorVersionsViewModel> {
    if (!ids.length) return {};
    const instances = await api.get<AllIndicatorVersionsViewModel>(`/dictionary/indicators/versions/all`, {
        params: { ids },
    });
    return instances.data;
}

export async function getIndicatorVersion(id: string, version: number): Promise<ConcreteIndicatorViewModel> {
    const indicators = await api.get<ConcreteIndicatorViewModel>(`/dictionary/indicators/${id}/${version}`);
    return indicators.data;
}

export const indicatorFromRefLoader = new Dataloader(
    async (refs: readonly IndicatorRef[]): Promise<(ConcreteIndicator | Error)[]> => {
        if (!refs.length) return [];

        const dictionary = await getConcreteIndicatorsByIds(refs.map((x) => x.id));

        return refs.map((ref): ConcreteIndicator | Error => {
            const entry = dictionary[ref.id];

            if (!entry) {
                return new Error(`Indicator with id ${ref.id} not found in response`);
            }

            if ('error' in entry) {
                return new Error(`Indicator with id ${ref.id}: ${entry.error}`);
            }

            return {
                ...ref,
                ...entry,
            } satisfies ConcreteIndicator;
        });
    },
    { cache: false },
);

/**
 * @deprecated Use indicatorFromRefLoader instead
 */
export async function getImportViewModelsFromRefs(refs: IndicatorImportRefViewModel[]): Promise<IndicatorImport[]> {
    if (!refs.length) return [];

    const results = await indicatorFromRefLoader.loadMany(refs);

    const indicatorImports: IndicatorImport[] = [];
    for (let i = 0; i < refs.length; i++) {
        const result = results[i];
        if (result instanceof Error) continue;

        indicatorImports.push({
            ...refs[i],
            ...result,
            alias: refs[i].alias,
        } satisfies IndicatorImport);
    }

    return indicatorImports;
}

export function getImportsStatuses(records: IndicatorRefViewModel[]): Observable<ImportsStatusesViewModel> {
    return rxapi.post<ImportsStatusesViewModel>(`/dictionary/indicators/status`, records).pipe(map((x) => x.data));
}

export function getIndicatorsFromSymbols(symbols: string[], key: string): Promise<ConcreteIndicator[]> {
    return api
        .get<ConcreteIndicator[]>(
            `/dictionary/indicators/by-symbol?${symbols.map((sym) => `symbols=${sym}`).join('&')}&${key ? `key=${key}` : ''}`,
        )
        .then((x) => x.data);
}

/**
 * @deprecated - Use getIndicatorsFromSymbols
 */
export function getIndicatorsFromSymbols$(symbols: string[], key: string): Observable<ConcreteIndicatorViewModel[]> {
    return rxapi
        .get<ConcreteIndicatorViewModel[]>(
            `/dictionary/indicators/by-symbol?${symbols.map((sym) => `symbols=${sym}`).join('&')}&${key ? `key=${key}` : ''}`,
        )
        .pipe(map((x) => x.data));
}

// todo: type this return
/**
 * @deprecated - No rxjs
 */
export function getIndicatorsFromCallNodes(key: string) {
    return pipe(
        bufferTime(100),
        filter((x) => !!x.length),
        mergeMap((nodes: CallInfo[]) =>
            getIndicatorsFromSymbols$(
                nodes.map((node) => node.source),
                key,
            ).pipe(catchError(() => of([]))),
        ),
    );
}

export async function deleteIndicator(id: string): Promise<void> {
    await api.delete<ConcreteIndicatorViewModel>(`/dictionary/indicators/${id}`);
}

export async function getIndicatorTags(filters: TagFilterViewModel = {}): Promise<TagViewModel[]> {
    const indicators = await api.get<TagViewModel[]>('/dictionary/tags', { params: filters });
    return indicators.data;
}

export function getIndicatorTags$(filters: TagFilterViewModel = {}): Observable<TagViewModel[]> {
    const indicators$ = rxapi.get<TagViewModel[]>('/dictionary/tags', { params: filters }).pipe(map((x) => x.data));
    return indicators$;
}

export function getIndicatorsBySlug(slugs: string[]): Promise<ConcreteIndicator[]> {
    return api.get(`/dictionary/indicators/by-slugs`, { params: { slug: slugs } }).then((res) => res.data);
}

export type SlugMapDict = Record<(typeof Slugs)[keyof typeof Slugs], ConcreteIndicator>;

export function constructIndicatorsBySlugMapQuery<T extends string>(
    ...slugs: T[]
): UseQueryOptions<Record<T, ConcreteIndicator>> {
    return {
        queryKey: ['indicators-by-slug', ...slugs],
        queryFn: () =>
            getIndicatorsBySlug(slugs).then((inds) =>
                inds.reduce(
                    (acc, ind) => {
                        acc[ind.matchSlug as T] = ind;
                        return acc;
                    },
                    {} as Record<T, ConcreteIndicator>,
                ),
            ),
    };
}

export async function getIndicatorSamples(id: string): Promise<Sample[]> {
    const indicators = await api.get<Sample[]>(`/dictionary/indicators/${id}/sample`);
    return indicators.data;
}
